import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";

export const fieldCls = "h-12 rounded-xl border-[#E6DBCF] bg-white/80 text-base text-[#2C1810] focus-visible:ring-[#C5A059] sm:text-sm";
export const labelCls = "text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]";

export function Field({ label, id, hint, children, ...props }) {
  return (
    <div className="block">
      <label htmlFor={id} className={labelCls}>{label}</label>
      <div className="mt-1.5">{children || <Input id={id} data-testid={id} {...props} className={fieldCls} />}</div>
      {hint && <p className="mt-1.5 text-xs text-[#6E5549]">{hint}</p>}
    </div>
  );
}

export function PasswordInput({ id, value, onChange, autoComplete = "current-password" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} data-testid={id} type={show ? "text" : "password"} value={value} onChange={onChange} required
        autoComplete={autoComplete} className={`${fieldCls} pr-12`} />
      <button type="button" onClick={() => setShow(!show)} data-testid={`${id}-toggle`} aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-lg text-[#6E5549] hover:text-[#2C1810] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C5A059]">
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

export const PW_RULES = [
  [(p) => p.length >= 8, "At least 8 characters"],
  [(p) => /[A-Za-z]/.test(p), "At least one letter"],
  [(p) => /\d/.test(p), "At least one number"],
];

export function PasswordRules({ password }) {
  return (
    <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-3" data-testid="password-requirements">
      {PW_RULES.map(([ok, text]) => (
        <li key={text} className={ok(password) ? "text-[#2E6B4F]" : "text-[#6E5549]"}>
          {ok(password) ? "✓" : "○"} {text}
        </li>
      ))}
    </ul>
  );
}

export const PrimaryButton = ({ className = "", children, ...props }) => (
  <button {...props} className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[#2C1810] px-7 text-sm font-semibold tracking-wide text-[#FDFBF7] transition-colors duration-200 hover:bg-[#3D2317] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C5A059] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}>
    {children}
  </button>
);

export const GhostButton = ({ className = "", children, ...props }) => (
  <button {...props} className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-[#E6DBCF] px-6 text-sm font-semibold text-[#2C1810] transition-colors duration-200 hover:bg-[#F6F1EA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C5A059] disabled:opacity-50 ${className}`}>
    {children}
  </button>
);
